import React from 'react';

const DocumentSection: React.FC = () => {
    return (
        <section className="py-20 bg-white">
            <div className="max-w-[1536px] mx-auto px-4 sm:px-6 lg:px-8">
                <div className="text-center mb-16">
                    <h2 className="text-3xl md:text-4xl font-bold text-primary mb-4">
                        Document AI
                    </h2>
                    <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                        Extract insights and automate document processing with advanced AI technology
                    </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                    {/* PDF to Excel Extraction */}
                    <div className="group relative bg-white border border-gray-200 rounded-2xl p-8 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 overflow-hidden">
                        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                        <div className="relative z-10">
                            <div className="w-16 h-16 bg-primary rounded-2xl transition-transform duration-300 flex items-center justify-center mb-6 shadow-lg shadow-primary/20 group-hover:scale-105">
                                <svg className="w-8 h-8 text-white transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v1a1 1 0 001 1h4a1 1 0 001-1v-1m3-2V8a2 2 0 00-2-2H8a2 2 0 00-2 2v6m3 0h6" />
                                </svg>
                            </div>
                            <h3 className="text-2xl font-bold text-gray-900 group-hover:text-primary transition-colors duration-300 mb-4">PDF to Excel Extraction</h3>

                            <div className="mb-4">
                                <h4 className="font-semibold text-gray-800 mb-2">Problem</h4>
                                <p className="text-gray-600">Manual data entry from PDF documents to spreadsheets</p>
                            </div>

                            <div className="mb-4">
                                <h4 className="font-semibold text-gray-800 mb-2">Solution</h4>
                                <p className="text-gray-600">AI-powered extraction of tables and data from PDF to Excel</p>
                            </div>

                            <div className="mb-4">
                                <h4 className="font-semibold text-gray-800 mb-2">Benefits</h4>
                                <ul className="text-gray-600 space-y-1">
                                    <li>• 95% accuracy</li>
                                    <li>• Save hours of work</li>
                                    <li>• Batch processing</li>
                                </ul>
                            </div>
                        </div>
                    </div>

                    {/* Resume Screening */}
                    <div className="group relative bg-white border border-gray-200 rounded-2xl p-8 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 overflow-hidden">
                        <div className="absolute inset-0 bg-gradient-to-br from-secondary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                        <div className="relative z-10">
                            <div className="w-16 h-16 bg-secondary rounded-2xl transition-transform duration-300 flex items-center justify-center mb-6 shadow-lg shadow-secondary/20 group-hover:scale-105">
                                <svg className="w-8 h-8 text-white transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                            </div>
                            <h3 className="text-2xl font-bold text-gray-900 group-hover:text-secondary transition-colors duration-300 mb-4">Resume Screening</h3>

                            <div className="mb-4">
                                <h4 className="font-semibold text-gray-800 mb-2">Problem</h4>
                                <p className="text-gray-600">Time-consuming resume review and inconsistent candidate evaluation</p>
                            </div>

                            <div className="mb-4">
                                <h4 className="font-semibold text-gray-800 mb-2">Solution</h4>
                                <p className="text-gray-600">AI-driven resume analysis and candidate ranking</p>
                            </div>

                            <div className="mb-4">
                                <h4 className="font-semibold text-gray-800 mb-2">Benefits</h4>
                                <ul className="text-gray-600 space-y-1">
                                    <li>• 75% time reduction</li>
                                    <li>• Better matching</li>
                                    <li>• Unbiased screening</li>
                                </ul>
                            </div>
                        </div>
                    </div>

                    {/* Invoice & ID Data Extraction */}
                    <div className="group relative bg-white border border-gray-200 rounded-2xl p-8 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 overflow-hidden">
                        <div className="absolute inset-0 bg-gradient-to-br from-dark/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                        <div className="relative z-10">
                            <div className="w-16 h-16 bg-dark rounded-2xl transition-transform duration-300 flex items-center justify-center mb-6 shadow-lg shadow-dark/20 group-hover:scale-105">
                                <svg className="w-8 h-8 text-white transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V8a2 2 0 00-2-2h-5m-4 0V5a2 2 0 114 0v1m-4 0a2 2 0 104 0m-5 8a2 2 0 100-4 2 2 0 000 4zm0 0c1.306 0 2.417.835 2.83 2M9 14a3.001 3.001 0 00-2.83 2M15 11h3m-3 4h2" />
                                </svg>
                            </div>
                            <h3 className="text-2xl font-bold text-gray-900 group-hover:text-dark transition-colors duration-300 mb-4">Invoice & ID Data Extraction</h3>

                            <div className="mb-4">
                                <h4 className="font-semibold text-gray-800 mb-2">Problem</h4>
                                <p className="text-gray-600">Manual data extraction from invoices and identity documents</p>
                            </div>

                            <div className="mb-4">
                                <h4 className="font-semibold text-gray-800 mb-2">Solution</h4>
                                <p className="text-gray-600">OCR and AI for automated data extraction from various document types</p>
                            </div>

                            <div className="mb-4">
                                <h4 className="font-semibold text-gray-800 mb-2">Benefits</h4>
                                <ul className="text-gray-600 space-y-1">
                                    <li>• High accuracy</li>
                                    <li>• Fast processing</li>
                                    <li>• Multiple formats</li>
                                </ul>
                            </div>
                        </div>
                    </div>

                    {/* Contract Analysis */}
                    <div className="group relative bg-white border border-gray-200 rounded-2xl p-8 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 overflow-hidden">
                        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                        <div className="relative z-10">
                            <div className="w-16 h-16 bg-primary rounded-2xl transition-transform duration-300 flex items-center justify-center mb-6 shadow-lg shadow-primary/20 group-hover:scale-105">
                                <svg className="w-8 h-8 text-white transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                            </div>
                            <h3 className="text-2xl font-bold text-gray-900 group-hover:text-primary transition-colors duration-300 mb-4">Contract Analysis</h3>

                            <div className="mb-4">
                                <h4 className="font-semibold text-gray-800 mb-2">Problem</h4>
                                <p className="text-gray-600">Risk of missing critical clauses and time-consuming legal review</p>
                            </div>

                            <div className="mb-4">
                                <h4 className="font-semibold text-gray-800 mb-2">Solution</h4>
                                <p className="text-gray-600">AI-powered contract analysis for risk assessment and clause extraction</p>
                            </div>

                            <div className="mb-4">
                                <h4 className="font-semibold text-gray-800 mb-2">Benefits</h4>
                                <ul className="text-gray-600 space-y-1">
                                    <li>• Risk identification</li>
                                    <li>• Clause extraction</li>
                                    <li>• Compliance checking</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default DocumentSection;
